import moment from "moment";

let appTimeZone = "Europe/Ljubljana";

export function setAppTimeZone(zone) {
  switch (zone) {
    default:
      appTimeZone = zone || appTimeZone;
  }
}

export function resolveBootstrapTimeZone(config) {
  return config?.useBrowserZone ? browserTimeZone() : config?.timezone;
}

// Backend only honors this when its useBrowserZone escape hatch is on (AppTimeZoneProvider) -
// otherwise it's sent but ignored in favor of the configured app timezone.
export function browserTimeZone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// Slovenian regional-settings convention. Exported so DateTimePicker.js's browser-local formatting
// can reuse the same pattern.
export function prettyDateFormat(withTime) {
  return withTime ? "DD.MM.YYYY HH:mm" : "DD.MM.YYYY";
}
function prettyDateFormatUnpadded(withTime) {
  return withTime ? "D.M.YYYY HH:mm" : "D.M.YYYY";
}
function prettyDateFormatCompact() {
  return "DDMMYYYY";
}

// Resolves the given zone's UTC offset (e.g. "+02:00") for the specific date, DST-correct.
function getUtcOffsetForZone(date, zone) {
  const offsetPart = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    timeZoneName: "longOffset"
  }).formatToParts(date).find(part => part.type === "timeZoneName");
  const offset = offsetPart ? offsetPart.value.replace("GMT", "") : "";
  return offset || "+00:00";
}

export function dateToString(date, dateOnly = false, pretty = false) {
  if (!date) {
    return "";
  }

  // moment(string) with no format hint falls back to the native Date() parser, which reads
  // dot-separated dates as US month.day.year - parse defensively with our own strict parser instead.
  if (typeof date === "string") {
    date = stringToDate(date, true) ?? stringToDate(date, false);
    if (!date) {
      return "";
    }
  }

  if (dateOnly) {
    return pretty ? dateToStringInZone(date, true) : moment(date).format("YYYY-MM-DD");
  }
  if (pretty) {
    return dateToStringInZone(date, false);
  }
  // Labels the picked digits with the configured zone's offset, not the viewer's own browser
  // offset, so "14:30" means "14:30 in the configured zone" for every viewer.
  return moment(date).format("YYYY-MM-DDTHH:mm:ss") + getUtcOffsetForZone(date, appTimeZone);
}

// Reads the wall-clock digits the configured zone (not the viewer's browser zone) shows for this
// instant. Used wherever a value needs to be compared/displayed in the same zone terms it was
// constructed in by dateToString()'s ISO branch above.
export function dateToStringInZone(date, dateOnly = false) {
  if (!date) {
    return "";
  }
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: appTimeZone,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(date).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const datePart = `${parts.day}.${parts.month}.${parts.year}`;
  return dateOnly ? datePart : `${datePart} ${parts.hour}:${parts.minute}`;
}

export function dateToTime(date) {
  if (!date) {
    return "";
  }

  return moment(date).format("HH:mm");
}

/**
 * @param Date date string (eg, '2022-10-15')
 * @returns {String} localized date string (eg. 'Oct 14, 2022')
 */
export function dateToLocalizedFormat(date) {
  return date != null ?
  moment(date).format("ll") : null;
}

/**
 * @param date HH:mm:ss ISO8601 time
 * @returns {String|null} HH:MM time or null if input string is null
 */
export function parseISOTime(time) {
  if (time == null) {
    return null;
  }
  const components = time.split(":");
  return `${components[0]}:${components[1]}`;
}

/**
 * @param startTime HH:mm time
 * @param endTime HH:mm time
 * @returns {boolean}
 */
export function isTimeGreaterThan(startTime, endTime) {
  return moment(startTime, 'HH:mm').diff(moment(endTime, 'HH:mm')) > 0;
}

/**
 * @param date String containing date and/or time
 * @param pretty Flag which represents if date is is pretty format ('D.M.YYYY HH:mm', 'D.M.YYYY') or in ISO format: ('YYYY-MM-DDTHH:mm:ssZ', 'YYYY-MM-DD')
 * @param hasTime If true return null if string does not have time included
 * @returns {Date|null|*} Date object or null if string is invalid date
 */
export function stringToDate(date, pretty = false, hasTime = false) {
  if (typeof date !== "string") {
    return date;
  }
  // moment's strict mode rejects zero-padded input against an unpadded "D.M..." pattern, so try
  // the zero-padded format first (matches dateToString's own output), then fall back to unpadded.
  let value = moment(date, pretty ? [prettyDateFormat(true), prettyDateFormatUnpadded(true)] : "YYYY-MM-DDTHH:mm:ssZ", true);
  if (!value.isValid() && !hasTime) {
    value = moment(date, pretty ? [prettyDateFormat(false), prettyDateFormatUnpadded(false)] : "YYYY-MM-DD", true);
    if (!value.isValid()) {
      value = moment(date, pretty ? prettyDateFormatCompact() : "YYYYMMDD", true);
    }
  }
  return value.isValid() ? value.toDate() : null;
}

export function fromNow(date) {
  if (!date) return "";
  date = stringToDate(date);
  return moment(date, undefined, "sl").fromNow();
}

export function toNow(date, skipPrefix = false) {
  date = stringToDate(date);
  return moment(date, undefined, "sl").toNow(skipPrefix);
}

export function duration(start, end, t = undefined) {
  start = stringToDate(start);
  end = stringToDate(end);
  const diff = moment(end).diff(moment(start));
  const duration = moment.duration(diff);
  if (t) {
    //if we have translation object we return detailed string
    const years = duration.years();
    const months = duration.months();
    const days = duration.days();
    if (years > 0) {
      return t("Years", {count: years}) + " " + t("Months", {count: months}) + " " + t("Days", {count: days});
    } else if (months > 0) {
      return t("Months", {count: months}) + " " + t("Days", {count: days});
    }
    return t("Days", {count: days});
  } else {
    return duration.locale("sl").humanize();
  }
}

export function durationInDays(start, end) {
  start = stringToDate(start);
  end = stringToDate(end);
  const diff = moment(end).diff(moment(start));
  const duration = moment.duration(diff);
  return duration.asDays();
}

/**
 * @returns String in format HH:mm
 */
export function toDuration(minutes){
  return Math.floor(minutes/60).toString().padStart(2, "0") + ":" +
    (minutes % 60).toString().padStart(2, "0");
}

/**
 * Remove seconds from time in string format
 * @param time String in format HH:mm:ss
 * @returns String in format HH:mm
 */
export function trimSeconds(time){
  return time.split(":", 2).join(":");
}

/**
 * Compares two dates without comparing the timestamps
 * @param date1 Date object
 * @param date2 Date object
 * @returns {boolean} True if date1 is smaller than date2 else False
 */
export function isDateSmallerThan(date1, date2) {
  date1 = new Date(date1.toDateString())
  date2 = new Date(date2.toDateString())
  return date1 < date2;
}

/**
 * Calculates number of days between two dates
 * @param date1 Date object
 * @param date2 Date object
 * @returns {number} difference between dates (in days)
 */
export function daysBetweenDates(date1, date2) {
  return Math.ceil((date1 - date2) / (1000 * 60 * 60 * 24));
}

/**
 * Checks if time is between start and end time
 * @param time
 * @param startTime
 * @param endTime
 * @returns {boolean} True if time is between start and end time, false otherwise
 */
export function isTimeBetween(time, startTime, endTime){
  if(startTime === endTime){
    return true;
  }
  if(isTimeGreaterThan(endTime, startTime)){
    return !(isTimeGreaterThan(startTime, time) ||
      isTimeGreaterThan(time, endTime));
  }
  return !(isTimeGreaterThan(startTime, time) &&
    isTimeGreaterThan(time, endTime));
}

function appendYear(date) {
  date.setFullYear(date.getFullYear() + 1);
  return date;
}

export function addDays(date, days) {
  return moment(stringToDate(date)).add(days, 'days').toDate();
}

export function subtractDays(date, days) {
  return moment(stringToDate(date)).add(-days, 'days').toDate();
}

const todayDate = new Date();
export const todayDateString = moment(new Date()).format("YYYY-MM-DD");
export const currentYear = new Date().getFullYear();
export const nextYear = appendYear(new Date());
export const yesterday = new Date(Date.now() - 86400000);
export const sevenDaysAgo = subtractDays(todayDate, 7);
export const thirtyDaysAgo = subtractDays(todayDate, 30);
export const today = subtractDays(todayDate, 0);
export function getTimePeriodOptions(baseDate = today) {
  const endDate = stringToDate(baseDate);
  const options = {
    "TODAY": {
      id: "today",
      code: "TODAY",
      name: "Today",
      fromDate: dateToString(endDate, true),
      toDate: dateToString(endDate, true)
    },
    "YESTERDAY": {
      id: "yesterday",
      code: "YESTERDAY",
      name: "Yesterday",
      fromDate: dateToString(subtractDays(endDate, 1), true),
      toDate: dateToString(subtractDays(endDate, 1), true)
    },
    "LAST_7_DAYS": {
      id: "last7days",
      code: "LAST_7_DAYS",
      name: "Last 7 Days",
      fromDate: dateToString(subtractDays(endDate, 7), true),
      toDate: dateToString(endDate, true)
    },
    "LAST_30_DAYS": {
      id: "last30days",
      code: "LAST_30_DAYS",
      name: "Last 30 Days",
      fromDate: dateToString(subtractDays(endDate, 30), true),
      toDate: dateToString(endDate, true)
    },
    "LAST_90_DAYS": {
      id: "last90days",
      code: "LAST_90_DAYS",
      name: "Last 90 Days",
      fromDate: dateToString(subtractDays(endDate, 90), true),
      toDate: dateToString(endDate, true)
    },
    "ALL_TIME": {
      id: "allTime",
      code: "ALL_TIME",
      name: "All Time",
      fromDate: null,
      toDate: null
    }
  };

  return options;
}